import { z } from 'zod';

export const jobCrewFeedbackSchema = z.object({
  job_crew_id: z.number().nullish(),
  appearance: z.boolean().default(true),
  appearance_comment: z.string().nullable().optional(),
  ppe: z.boolean().default(true),
  photo_id: z.boolean().default(true),
  // Added for the Oct 2026 offence list. TRUE means fine, like every other toggle here —
  // a strike is the false case, and a question nobody answered must never read as one.
  no_unauthorised_smoke_break: z.boolean().default(true),
  no_excessive_phone_use: z.boolean().default(true),
  ppe_comment: z.string().nullable().optional(),
  attitude: z.boolean().default(true),
  attitude_comment: z.string().nullable().optional(),
  on_time: z.boolean().default(true),
  fifteen_mins_early: z.boolean().default(true),
  fifteen_mins_early_comment: z.string().nullable().optional(),
  arrival_status: z.enum(["arrived_10_mins_early_prepared", "arrived_within_10_mins_prepared", "arrived_before_call_not_prepared", "late_arrival"]).nullable().optional(),
  late_0_to_15: z.boolean().default(false),
  late_15_to_30: z.boolean().default(false),
  late_30_to_45: z.boolean().default(false),
  late_1hr: z.boolean().default(false),
  no_show: z.boolean().default(false),
  late_time: z.string().nullable().optional(),
  client_aware: z.boolean().default(false),
  make_up_time: z.boolean().default(true),
  make_up_time_comment: z.string().nullable().optional(),
  job_affected: z.boolean().default(false),
  job_affected_comment: z.string().nullable().optional(),
  client_upset: z.boolean().default(false),
  client_upset_comment: z.string().nullable().optional(),
  lx: z.number().int().positive().nullable().optional(),
  video: z.number().int().positive().nullable().optional(),
  truck_packing: z.number().int().positive().nullable().optional(),
  set_stage: z.number().int().positive().nullable().optional(),
  leadership: z.number().int().positive().nullable().optional(),
  communication: z.number().int().positive().nullable().optional(),
  physicality: z.number().int().positive().nullable().optional(),
  plant_operation: z.number().int().positive().nullable().optional(),
  set_building: z.number().int().positive().nullable().optional(),
  overall_rating: z.number().int().positive().min(1, { message: "Please select a rating" }).max(5, { message: "Please select a rating" }),
  overall_rating_comments: z.string().nullable().optional(),
  working_at_level: z.number().int().min(0, { message: "Please select a working level" }).max(6, { message: "Please select a working level" }),
  rating_comments: z.string().min(1, { message: "Please add a comment" }),
  hide_average: z.boolean().default(false)
}).refine(data => data.ppe || (data.ppe_comment && data.ppe_comment.length > 0), {
  message: "Please add a comment for ppe",
  path: ["ppe_comment"],
}).refine(data => data.attitude || (data.attitude_comment && data.attitude_comment.length > 0), {
  message: "Please add a comment for attitude",
  path: ["attitude_comment"],
}).refine(data => data.appearance || (data.appearance_comment && data.appearance_comment.length > 0), {
  message: "Please add a comment for appearance",
  path: ["appearance_comment"],
}).refine(data => {
  // If overall rating is 1, 2, or 5, comments are required
  if ([1, 2, 5].includes(data.overall_rating)) {
    return data.overall_rating_comments && data.overall_rating_comments.length > 0;
  }
  return true;
}, {
  message: "Please add a comment for this rating",
  path: ["overall_rating_comments"],
});


export type JobCrewFeedbackFormValues = z.infer<typeof jobCrewFeedbackSchema>;


export const mapFeedbackForBackend = (values: JobCrewFeedbackFormValues, lateValue: keyof JobCrewFeedbackFormValues, author_id: number) => {
  return {
    ...values,
    job_crew_id: values.job_crew_id || 0,
    [lateValue]: true,
    author_id
  }
}

export const defaultJobCrewFeedbackValues: JobCrewFeedbackFormValues = {
  appearance: true,
  appearance_comment: '',
  ppe: true,
  photo_id: true,
  // TRUE means fine. These seed the form — the zod .default(true) above only applies when
  // parsing, not to react-hook-form's defaults, so leaving them out here rendered both
  // switches off, which reads as the offence having happened. Every clean feedback a chief
  // submitted would have issued two strikes.
  no_unauthorised_smoke_break: true,
  no_excessive_phone_use: true,
  ppe_comment: '',
  attitude: true,
  attitude_comment: '',
  on_time: true,
  fifteen_mins_early: true,
  fifteen_mins_early_comment: '',
  arrival_status: null,
  late_0_to_15: false,
  late_15_to_30: false,
  late_30_to_45: false,
  late_time: null,
  late_1hr: false,
  no_show: false,
  client_aware: false,
  make_up_time: true,
  make_up_time_comment: '',
  job_affected: false,
  job_affected_comment: '',
  client_upset: false,
  client_upset_comment: '',
  lx: null,
  video: null,
  truck_packing: null,
  set_stage: null,
  leadership: null,
  communication: null,
  physicality: null,
  plant_operation: null,
  set_building: null,
  rating_comments: '',
  overall_rating: 0,
  overall_rating_comments: '',
  working_at_level: -1, // Invalid default to force selection
  hide_average: false
};

export const ratingFields = {
  lx: 'LX',
  video: 'Video',
  truck_packing: 'Truck Packing',
  set_stage: 'Set Stage',
  leadership: 'Leadership',
  communication: 'Communication',
  physicality: 'Physicality',
  plant_operation: 'Plant Operation',
  set_building: 'Set Building',
}